import { getFirestore, collection, doc, addDoc, serverTimestamp, query, orderBy, getDocs, Timestamp, getDoc, updateDoc, setDoc, where, limit } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../config/firebaseConfig";
import { GAME_SETTINGS, GAME_STATES, GAME_CONFIG } from "../config/gameConfig";
import { RHYTHM_CONFIG } from "../config/RhythmConfig";
import { parseTime, parseCreatedAt } from "./timeUtils";

const databaseId = process.env.REACT_APP_FIREBASE_DB_ID; // 'default'로 강제 설정
const db = getFirestore(app, databaseId);
const functions = getFunctions(app, "asia-northeast3");
const EVENT_ID = GAME_CONFIG.EVENT_ID;

// Cloud Functions 호출 함수들
const getTopRankingCloud = httpsCallable(functions, 'getTopRanking');
const updateLeaderboardCloud = httpsCallable(functions, 'updateLeaderboard');
const getLeaderboardRankingCloud = httpsCallable(functions, 'getLeaderboardRanking');
const initializeLeaderboardCloud = httpsCallable(functions, 'initializeLeaderboard');
 
// 🔥 고동시접속 대응을 위한 연결 풀 관리
class ConnectionPool {
  constructor() {
    this.activeConnections = 0;
    this.maxConnections = 50; // 최대 동시 연결 수
    this.queue = [];
    this.isProcessing = false;
  }

  async execute(operation) {
    return new Promise((resolve, reject) => {
      const task = { operation, resolve, reject };
      
      if (this.activeConnections < this.maxConnections) {
        this.executeTask(task);
      } else {
        this.queue.push(task);
      }
    });
  }

  async executeTask(task) {
    this.activeConnections++;
    
    try {
      const result = await task.operation();
      task.resolve(result);
    } catch (error) {
      task.reject(error);
    } finally {
      this.activeConnections--;
      this.processQueue();
    }
  }

  processQueue() {
    if (this.queue.length > 0 && this.activeConnections < this.maxConnections) {
      const task = this.queue.shift();
      this.executeTask(task);
    }
  }
}

// 전역 연결 풀 인스턴스
const connectionPool = new ConnectionPool();

// 🔥 요청 제한 (Rate Limiting)
class RateLimiter {
  constructor() {
    this.requests = new Map();
    this.maxRequests = 10; // 10초당 최대 요청 수
    this.windowMs = 10000; // 10초 윈도우
  }

  canMakeRequest(identifier) {
    const now = Date.now();
    const userRequests = this.requests.get(identifier) || [];
    
    // 윈도우 밖의 요청들 제거
    const validRequests = userRequests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    return true;
  }

  clearOldRequests() {
    const now = Date.now();
    for (const [identifier, requests] of this.requests.entries()) {
      const validRequests = requests.filter(time => now - time < this.windowMs);
      if (validRequests.length === 0) {
        this.requests.delete(identifier);
      } else {
        this.requests.set(identifier, validRequests);
      }
    }
  }
}

const rateLimiter = new RateLimiter();

// 주기적으로 오래된 요청 기록 정리
setInterval(() => {
  rateLimiter.clearOldRequests();
}, 30000); // 30초마다 정리

// 🔥 안전한 Firestore 작업 래퍼
const safeFirestoreOperation = async (operation, identifier = 'default') => {
  // 요청 제한 체크
  if (!rateLimiter.canMakeRequest(identifier)) {
    throw new Error('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
  }

  // 연결 풀을 통한 실행
  return connectionPool.execute(operation);
};

// 게임 결과 저장 함수
export async function submitScore(data) {
  const { gameCollection, ...saveData } = data;
  
  const result = await safeFirestoreOperation(async () => {
    // gameCollection을 사용하여 각 게임별로 분리된 컬렉션에 저장
    const scoresRef = collection(db, "gameEvent", EVENT_ID, gameCollection);
    const docRef = await addDoc(scoresRef, saveData);
    return docRef.id; // 생성된 문서의 ID 반환
  }, `submitScore_${gameCollection}`);
  
  // 집계 데이터 업데이트 (백그라운드에서 실행, 실패해도 원본 저장은 성공)
  try {
    const gameType = Object.keys(GAME_SETTINGS).find(key => 
      GAME_SETTINGS[key].resultCollection === gameCollection
    );
    
    if (gameType) {
      // 집계 데이터에 저장할 플레이어 데이터 준비
      const playerData = {
        ...saveData,
        resultId: result, // 원본 결과 문서 ID
        gameType: gameType
      };
      
      // Cloud Functions를 통해 집계 데이터 업데이트
      await updateLeaderboardCloud({
        gameType,
        eventId: EVENT_ID,
        databaseId: databaseId,
        playerData
      });
      
      // console.log(`[submitScore] 집계 데이터 업데이트 완료: ${gameType}`);
    }
      } catch (error) {
      // console.warn(`[submitScore] 집계 데이터 업데이트 실패 (무시됨):`, error);
    }
  
  return result;
}

// 랭킹 조회 함수 (집계 데이터 직접 조회)
export async function getTopRanking(gameType) {
  // console.log(`[getTopRanking] 시작: ${gameType}`);
  const startTime = performance.now();
  
  try {
    // 집계 데이터에서 직접 조회
    const leaderboardRef = collection(db, "gameEvent", EVENT_ID, "leaderboards", gameType, "players");
    // console.log(`[getTopRanking] 집계 데이터 조회: gameEvent/${EVENT_ID}/leaderboards/${gameType}/players`);
    
    let q;
    
    // 게임별 정렬 기준에 따라 쿼리 구성
    if (gameType === GAME_STATES.RHYTHM_GAME) {
      // 리듬: 점수 desc > 콤보 desc > 등록시간 asc
      q = query(leaderboardRef, 
        orderBy('latestData.score', 'desc'),
        orderBy('latestData.maxCombo', 'desc'),
        orderBy('latestData.createdAt', 'asc'),
        limit(20)
      );
    } else if (gameType === GAME_STATES.PUZZLE_GAME) {
      // 퍼즐: 시간 asc > 움직임 asc > 등록시간 asc
      q = query(leaderboardRef,
        orderBy('latestData.timeSpentMs', 'asc'),
        orderBy('latestData.moves', 'asc'),
        orderBy('latestData.createdAt', 'asc'),
        limit(20)
      );
    } else if (gameType === GAME_STATES.DINO_GAME) {
      // 공룡: 점수 desc > 시간 desc > 등록시간 asc
      q = query(leaderboardRef,
        orderBy('latestData.score', 'desc'),
        orderBy('latestData.timeSpentMs', 'desc'),
        orderBy('latestData.createdAt', 'asc'),
        limit(20)
      );
    } else if (gameType === GAME_STATES.WORLDCUP_GAME) {
      // 월드컵은 영상별 집계가 필요하므로 함수 사용
      // console.log(`[getTopRanking] 월드컵은 함수 사용`);
      const result = await getTopRankingCloud({ 
        gameType,
        eventId: EVENT_ID,
        databaseId: databaseId
      });
      const response = result.data;
      const totalTime = performance.now() - startTime;
      // console.log(`[getTopRanking] 월드컵 완료: ${totalTime.toFixed(2)}ms`);
      return response.data;
    } else {
      // 기본: 점수 desc
      q = query(leaderboardRef,
        orderBy('latestData.score', 'desc'),
        limit(20)
      );
    }
    
    const snapshot = await getDocs(q);
    const players = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.latestData) {
        players.push({
          id: doc.id,
          ...data.latestData
        });
      }
    });
    
    // 서버에서 이미 정렬된 데이터이므로 클라이언트 정렬 불필요
    // console.log(`[getTopRanking] 서버 정렬 완료: ${gameType}, ${players.length}명`);
    
    // 상위 20개만 반환 (서버에서 이미 limit(20) 적용됨)
    const top20 = players.slice(0, 20);
    
    const totalTime = performance.now() - startTime;
    // console.log(`[getTopRanking] 직접 조회 완료: ${gameType}, ${top20.length}명, ${totalTime.toFixed(2)}ms`);
    
    // console.log(`[getTopRanking] 집계 데이터 조회 성공: ${top20.length}명`);
    return top20;
  } catch (error) {
    // console.error(`[getTopRanking] 직접 조회 실패:`, error);
    
    // 실패 시 기존 방식으로 폴백
    // console.log(`[getTopRanking] 폴백: 원본 컬렉션 조회`);
    return await getTopRankingFromFirestore(gameType);
  }
}
  
// 기존 Firestore 조회 로직을 별도 함수로 분리 (폴백용)
async function getTopRankingFromFirestore(gameType) {
  // 기존 getTopRanking 로직 그대로 유지
  // console.log(`[getTopRanking] Firestore 직접 조회 시작: ${gameType}`);
  const startTime = performance.now();
  
  // 게임 타입에 따라 다른 컬렉션 사용
  const settings = GAME_SETTINGS[gameType];
  const collectionName = settings.resultCollection;

  const scoresRef = collection(db, "gameEvent", EVENT_ID, collectionName);
  
  // groupBy를 사용하여 각 phone별로 최신 데이터만 가져오기
  const q = query(
    scoresRef,
    orderBy('phone', 'asc'),
    orderBy('createdAt', 'desc')
  );
  
  let snapshot;
  try {
    // console.log(`[getTopRanking] Firestore 쿼리 시작: ${collectionName}`);
    const queryStartTime = performance.now();
    snapshot = await getDocs(q);
    const queryEndTime = performance.now();
    // console.log(`[getTopRanking] Firestore 쿼리 완료: ${(queryEndTime - queryStartTime).toFixed(2)}ms, 문서 수: ${snapshot.size}`);
  } catch (error) {
    throw error;
  }

  // groupBy 결과에서 각 phone별로 첫 번째(최신) 데이터만 추출
  // console.log(`[getTopRanking] 클라이언트 처리 시작`);
  const processStartTime = performance.now();
  const userLast = {};
  snapshot.forEach(doc => {
    const data = doc.data();
    const phone = data.phone;
    
    // 각 phone별로 첫 번째(최신) 데이터만 저장
    if (!userLast[phone]) {
      userLast[phone] = { ...data };
    }
  });
  const processEndTime = performance.now();
  // console.log(`[getTopRanking] 클라이언트 처리 완료: ${(processEndTime - processStartTime).toFixed(2)}ms, 사용자 수: ${Object.keys(userLast).length}`);

  // 정렬: 게임 타입별로 다른 정렬 로직
  if (gameType === GAME_STATES.WORLDCUP_GAME) {
    // 월드컵 게임: 영상별로 그룹화하고 선택된 횟수로 정렬
    const videoGroups = {};
    
    // 각 사용자의 최신 데이터를 영상별로 그룹화
    Object.values(userLast).forEach(userData => {
      const videoId = userData.finalVideoId;
      const title = userData.title;
      
      if (videoId && title) {
        if (!videoGroups[videoId]) {
          videoGroups[videoId] = {
            title: title,
            videoId: videoId,
            winnerCount: 0,
            users: []
          };
        }
        videoGroups[videoId].winnerCount += 1;
        videoGroups[videoId].users.push(userData);
      }
    });
    
    // 모든 월드컵 영상 정보 가져오기
    try {
      const allVideos = await getWorldCupVideos();
      if (allVideos) {
        // 모든 영상을 포함하도록 videoGroups 업데이트
        allVideos.forEach(video => {
          // videoId로 중복 체크 (사용자 데이터의 finalVideoId와 allVideos의 id가 다를 수 있음)
          const existingVideo = Object.values(videoGroups).find(v => v.videoId === video.id || v.title === video.title);
          if (!existingVideo) {
            videoGroups[video.id] = {
              title: video.title,
              videoId: video.id,
              winnerCount: 0,
              users: []
            };
          }
        });
      }
    } catch (error) {
      //console.error("월드컵 영상 정보 조회 실패:", error);
    }
    
    // 선택된 횟수로 정렬 (내림차순), 선택자가 없으면 영상 제목 순으로 정렬
    // console.log(`[getTopRanking] 월드컵 정렬 시작`);
    const sortStartTime = performance.now();
    const result = Object.values(videoGroups)
      .sort((a, b) => {
        // 선택자가 있는 경우 선택자 수로 정렬 (내림차순)
        if (a.winnerCount > 0 || b.winnerCount > 0) {
          if (b.winnerCount !== a.winnerCount) {
            return b.winnerCount - a.winnerCount;
          }
        }
        // 선택자 수가 같거나 모두 0인 경우 영상 제목으로 정렬 (오름차순)
        return a.title.localeCompare(b.title);
      })
      .map(video => ({
        title: video.title,
        videoId: video.videoId,
        winnerCount: video.winnerCount,
        users: video.users
      }));
    const sortEndTime = performance.now();
    // console.log(`[getTopRanking] 월드컵 정렬 완료: ${(sortEndTime - sortStartTime).toFixed(2)}ms`);
    
    const totalTime = performance.now() - startTime;
    // console.log(`[getTopRanking] 전체 완료: ${totalTime.toFixed(2)}ms`);
    
    return result;
  } else {
    // 다른 게임들: 기존 로직 유지
    // console.log(`[getTopRanking] 게임별 정렬 시작: ${gameType}`);
    const sortStartTime = performance.now();
    const result = Object.values(userLast).sort((a, b) => {
      /// 리듬은 점수 > 최고콤보 > 등록시간 순으로 정렬 (필드 매핑 적용)
      if (gameType === GAME_STATES.RHYTHM_GAME) {
         const aScore = a.totalScore || a.score || 0;
         const bScore = b.totalScore || b.score || 0;
         const aCombo = a.totalCombo || a.maxCombo || a.combo || 0;
         const bCombo = b.totalCombo || b.maxCombo || b.combo || 0;
         
         if (bScore !== aScore) return bScore - aScore;
         if (bCombo !== aCombo) return bCombo - aCombo;
         return parseCreatedAt(a.createdAt) - parseCreatedAt(b.createdAt);
        
      /// 퍼즐은 시간 > 횟수 > 등록시간 순으로 정렬  
        } else if (gameType === GAME_STATES.PUZZLE_GAME) {
          if (a.timeSpentMs !== b.timeSpentMs) {
            return a.timeSpentMs - b.timeSpentMs;
          }
          if (a.moves !== b.moves) return a.moves - b.moves;
          return parseCreatedAt(a.createdAt) - parseCreatedAt(b.createdAt);

      /// 공룡게임은 점수 > 시간 > 먼저 등록한 순으로 정렬
      } else if (gameType === GAME_STATES.DINO_GAME) {
          if (b.score !== a.score) return b.score - a.score;
          if (a.timeSpentMs !== b.timeSpentMs) {
            return a.timeSpentMs - b.timeSpentMs;
          }
          return parseCreatedAt(a.createdAt) - parseCreatedAt(b.createdAt);
      }
      // 기본값: 점수 기준 내림차순
      return (b.score || 0) - (a.score || 0);
    });
    const sortEndTime = performance.now();
    // console.log(`[getTopRanking] 게임별 정렬 완료: ${(sortEndTime - sortStartTime).toFixed(2)}ms`);
    
    const totalTime = performance.now() - startTime;
    // console.log(`[getTopRanking] 전체 완료: ${totalTime.toFixed(2)}ms`);
    
    return result;
  }
} 

// 상품 목록 조회 함수
export async function getGiftProducts() {
  try {
    const giftsRef = doc(db, "gameEvent", EVENT_ID, "config", "gifts");
    const giftsDoc = await getDoc(giftsRef);
    
    if (giftsDoc.exists()) {
      const data = giftsDoc.data();
      return data.products || [];
    } else {
      //console.log('상품 데이터가 존재하지 않습니다.');
      return [];
    }
  } catch (error) {
    //console.error('상품 목록 조회 중 에러 발생:', error);
    return [];
  }
}

// 룰렛 상품 정보 조회 함수
export async function getRoulettePrizes() {
  try {
    const rouletteRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.ROULETTE);
    const docSnap = await getDoc(rouletteRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } 
    return null;
  } catch (error) {
    //console.error("룰렛 상품 정보 조회 실패:", error);
    throw error;
  }
}

// 룰렛 당첨 결과 저장 함수
export async function saveRouletteResult({ nickname, prizeName, phone }) {
  try {
    // phone 값이 없으면 저장하지 않음
    if (!phone) {
      //console.error('전화번호가 없어서 룰렛 결과를 저장할 수 없습니다.');
      throw new Error('전화번호가 필요합니다.');
    }
    
    // 중복 제출 방지 (로컬스토리지 체크)
    const submissionKey = `roulette_${phone}_${Date.now()}`;
    const lastSubmission = localStorage.getItem('lastRouletteSubmission');
    if (lastSubmission && Date.now() - parseInt(lastSubmission) < 5000) {
      throw new Error('너무 빠른 제출입니다. 잠시 후 다시 시도해주세요.');
    }
    localStorage.setItem('lastRouletteSubmission', Date.now().toString());
    
    return safeFirestoreOperation(async () => {
      const resultRef = collection(db, "gameEvent", EVENT_ID, GAME_STATES.ROULETTE_RESULT);
      
      // 타임아웃 설정 (10초)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('저장 시간 초과')), 10000);
      });
      
      const savePromise = addDoc(resultRef, {
        nickname,
        prizeName,
        phone, // 사용자 핸드폰 번호로 추적
        timestamp: serverTimestamp()
      });

      // 타임아웃과 저장 작업을 경쟁시킴
      await Promise.race([savePromise, timeoutPromise]);

      // 상품별 당첨자 수 체크 및 확률 업데이트 (백그라운드에서 실행)
      checkAndUpdatePrizeProbability(prizeName).catch(error => {
        //console.warn('확률 업데이트 실패 (무시됨):', error);
      });
    }, `roulette_${phone}`);
    
  } catch (error) {
    //console.error("룰렛 당첨 결과 저장 실패:", error);
    
    // 연결 제한 오류인 경우 사용자에게 알림
    if (error.code === 'resource-exhausted' || error.code === 'unavailable') {
      throw new Error('서버가 혼잡합니다. 잠시 후 다시 시도해주세요.');
    }
    
    throw error;
  }
}

/**
 * 룰렛 결과 조회 함수 (5분 전까지만)
 * @returns {Promise<Array>} 룰렛 결과 배열
 */
export async function getRouletteResults() {
  try {
    // 5분 전 시간 계산 (서버 시간 기준)
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
    
    const resultRef = collection(db, "gameEvent", EVENT_ID, GAME_STATES.ROULETTE_RESULT);
    const q = query(
      resultRef,
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const results = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      // parseCreatedAt은 UTC timestamp를 밀리초로 변환
      const timestamp = parseCreatedAt(data.timestamp);
      
      // UTC 기준 5분 전 이후의 결과만 필터링
      if (timestamp >= fiveMinutesAgo.getTime()) {
        results.push({
          id: doc.id,
          nickname: data.nickname,
          prizeName: data.prizeName,
          timestamp: timestamp
        });
      }
    });
    
    // 오래된순으로 정렬
    return results.sort((a, b) => a.timestamp - b.timestamp);
    
  } catch (error) {
    //console.error("룰렛 결과 조회 실패:", error);
    return [];
  }
}

// 상품별 당첨자 수 체크 및 확률 업데이트 함수
export async function checkAndUpdatePrizeProbability(prizeName) {
  try {
    // 1. 룰렛 정보 조회
    const rouletteRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.ROULETTE);
    const rouletteDoc = await getDoc(rouletteRef);
    
    if (!rouletteDoc.exists()) {
      //console.error("룰렛 정보를 찾을 수 없습니다.");
      return;
    }

    const rouletteData = rouletteDoc.data();
    const prizes = rouletteData.prizes;

    // 2. 해당 상품의 수량 제한 찾기
    const targetPrize = prizes.find(prize => prize.option === prizeName && !prize.isLose);
    if (!targetPrize || !targetPrize.maxQuantity) {
      // 수량 제한이 없으면 업데이트 불필요
      return;
    }

    // 3. 해당 상품의 현재 당첨자 수 조회
    const resultRef = collection(db, "gameEvent", EVENT_ID, GAME_STATES.ROULETTE_RESULT);
    const snapshot = await getDocs(resultRef);
    
    let currentWinners = 0;
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.prizeName === prizeName) {
        currentWinners++;
      }
    });

    // 4. 수량 제한에 도달했는지 확인
    if (currentWinners >= targetPrize.maxQuantity) {
      //console.log(`상품 "${prizeName}"의 수량 제한(${targetPrize.maxQuantity}개)에 도달했습니다. 확률을 0으로 업데이트합니다.`);
      
      // 5. 해당 상품의 확률을 0으로 업데이트하고, 그 확률을 꽝에 더하기
      const updatedPrizes = prizes.map(prize => {
        if (prize.option === prizeName && !prize.isLose) {
          return { ...prize, probability: 0 };
        }
        return prize;
      });

      // 6. 꽝의 확률을 찾아서 기존 상품의 확률을 더하기
      const losePrize = updatedPrizes.find(prize => prize.isLose);
      if (losePrize) {
        losePrize.probability += targetPrize.probability;
      }

      // 7. 룰렛 정보 업데이트
      await updateDoc(rouletteRef, {
        prizes: updatedPrizes
      });

      //console.log(`상품 "${prizeName}"의 확률이 0으로 업데이트되고, 꽝의 확률이 ${targetPrize.probability}만큼 증가했습니다.`);
    }
  } catch (error) {
    //console.error("상품 확률 업데이트 실패:", error);
    // 확률 업데이트 실패해도 당첨 결과 저장은 성공했으므로 에러를 던지지 않음
  }
}

// 특정 사용자의 룰렛 당첨 여부 확인 함수
export async function hasUserWonRoulette(phone) {
  try {
    const resultRef = collection(db, "gameEvent", EVENT_ID, GAME_STATES.ROULETTE_RESULT);
    const snapshot = await getDocs(resultRef);
    
    // 해당 phone으로 당첨 기록이 있는지 확인
    const hasWon = snapshot.docs.some(doc => {
      const data = doc.data();
      return data.phone === phone;
    });
    
    return hasWon;
  } catch (error) {
    //console.error("사용자 당첨 여부 확인 실패:", error);
    throw error;
  }
}
/**
 * 이벤트 시작일자(start_date) 읽기
 * @returns {Promise<string|null>} ISO 날짜 문자열 (예: '2024-07-25')
 */
export async function getEventStartDate() {
  try {
    const docRef = doc(db, "gameEvent", EVENT_ID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().start_date || null;
    }
    return null;
  } catch (error) {
    throw error;
  }
}

/**
 * 출석 기록 추가 (오늘 날짜)
 * @param {string} userId
 * @returns {Promise<void>}
 */
export async function addAttendance(userId) {
  try {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const attendanceRef = doc(db, "gameEvent", EVENT_ID, "users", userId, "attendance", dateStr);
    await setDoc(attendanceRef, { date: dateStr, timestamp: serverTimestamp() });
  } catch (error) {
    throw error;
  }
}

/**
 * 출석 내역 조회 (모든 날짜)
 * @param {string} userId
 * @returns {Promise<string[]>} ISO 날짜 문자열 배열
 */
export async function getAttendanceList(userId) {
  try {
    const attendanceCol = collection(db, "gameEvent", EVENT_ID, "users", userId, "attendance");
    const snapshot = await getDocs(attendanceCol);
    return snapshot.docs.map(doc => doc.id).sort();
  } catch (error) {
    throw error;
  }
}

/**
 * Firestore Timestamp 또는 JS Date, 밀리초 숫자를 KST 기준 ISO 문자열(YYYY-MM-DDTHH:mm:ss)로 변환
 * @param {import("firebase/firestore").Timestamp|Date|number} ts
 * @returns {string} "2025-07-15T19:45:30"
 */
export const toKstIso = ts => {
  // Firestore Timestamp 객체라면 toMillis(), Date 객체라면 getTime(), 숫자(밀리초)라면 그대로
  const ms = ts?.toMillis
    ? ts.toMillis()
    : ts instanceof Date
      ? ts.getTime()
      : ts;
  // KST = UTC+9h
  const kstMs = ms + 9 * 60 * 60 * 1000;
  // toISOString() → "YYYY-MM-DDTHH:mm:ss.sssZ", slice으로 밀리초·Z 제거
  return new Date(kstMs).toISOString();
};

// 사용 예 (Firestore Timestamp라 가정)
// const submittedAt = docSnap.data().submittedAt;
// console.log(toKstIso(submittedAt));  // "2025-07-15T19:45:30"

/**
 * 각 게임별(phone 기준) 참여 여부 조회
 * @param {string} phone
 * @returns {Promise<{quiz: boolean, worldcup: boolean, puzzle: boolean, rhythm: boolean}>}
 */
export async function getUserEventStamps(phone) {
  //console.log('getUserEventStamps', phone);
  const eventId = EVENT_ID;
  const dbPaths = [
    { key: 'quiz', col: 'quiz_result' },
    { key: 'worldcup', col: 'worldcup_result' },
    { key: 'puzzle', col: 'puzzle_result' },
    { key: 'rhythm', col: 'rhythm_result' },
  ];
  const result = {};
  for (const { key, col } of dbPaths) {
    const colRef = collection(db, 'gameEvent', eventId, col);
    const q = query(colRef);
    const snapshot = await getDocs(q);
    // phone 참여 여부 확인
    const found = snapshot.docs.some(doc => {
      const data = doc.data();
      //console.log(`[${col}] 문서ID:`, doc.id, 'phone:', data.phone, '비교:', phone);
      return data.phone === phone;
    });
    result[key] = found;
  }
  return result;
}

/**
 * 월드컵 영상 정보 조회 함수
 * @returns {Promise<Array|null>} 영상 정보 배열 또는 null
 */
export async function getWorldCupVideos() {
  try {
    const worldcupRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.WORLDCUP_GAME);
    const docSnap = await getDoc(worldcupRef);
    
    if (docSnap.exists()) {
      return docSnap.data().shorts || null;
    } 
    return null;
  } catch (error) {
    //console.error("월드컵 영상 정보 조회 실패:", error);
    throw error;
  }
}

/**
* 월드컵 우승 영상 선택자 수 조회 함수
* @param {string} finalVideoId
* @returns {Promise<number>} 우승 영상 선택자 수
*/
export async function getWorldCupWinnerCount(finalVideoId) {
  try {
    const worldcupSettings = GAME_SETTINGS.worldcup;
    const resultsRef = collection(db, "gameEvent", EVENT_ID, worldcupSettings.resultCollection);
    const q = query(resultsRef, orderBy('createdAt', 'desc')); // 최신 등록 순으로 정렬
    
    const snapshot = await getDocs(q);
    
    // phone 기준으로 최신 결과만 추출 (중복 제거)
    const userLast = {};
    snapshot.forEach(doc => {
      const data = doc.data();
      const key = data.phone; // phone으로 중복 제거
      
      if (!userLast[key]) {
        userLast[key] = { ...data };
      } else {
        // 마지막 결과만 유지 (createdAt이 더 큰 것이 최신)
        if (data.createdAt?.seconds > userLast[key].createdAt?.seconds) {
          userLast[key] = { ...data };
        }
      }
    });
    
    // 해당 영상을 선택한 사용자 수 계산
    const winnerCount = Object.values(userLast).filter(result => 
      result.finalVideoId === finalVideoId
    ).length;
    
    return winnerCount;
  } catch (error) {
    //console.error("월드컵 우승자 수 조회 실패:", error);
    return 0;
  }
}

/**
 * 퀴즈 문제 정보 조회 함수
 * @returns {Promise<Array|null>} 퀴즈 문제 배열 또는 null
 */
export async function getQuizQuestions() {
  try {
    const quizRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.QUIZ_GAME);
    const docSnap = await getDoc(quizRef);
    
    if (docSnap.exists()) {
      return docSnap.data().questions || null;
    } 
    return null;
  } catch (error) {
    //console.error("퀴즈 문제 정보 조회 실패:", error);
    throw error;
  }
}

/**
 * 드립 문제 정보 조회 함수
 * @returns {Promise<Array|null>} 드립 문제 배열 또는 null
 */
export async function getDripQuestions() {
  try {
    const quizRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.DRIP_BATTLE);
    const docSnap = await getDoc(quizRef);
    
    if (docSnap.exists()) {
      return docSnap.data().questions || null;
    } 
    return null;
  } catch (error) {
    //console.error("드립 정보 조회 실패:", error);
    throw error;
  }
}

/**
 * 드립 댓글 조회 함수
 * @param {string} videoId - YouTube 비디오 ID
 * @returns {Promise<Array|null>} 댓글 배열 또는 null
 */
export async function getDripComments(videoId) {
  try {
    // 1. videoId 컬렉션에서 lastFetchedAt 필드 조회
    const videoRef = doc(db, "gameEvent", EVENT_ID, "DripComment", videoId);
    const videoDoc = await getDoc(videoRef);
    
    if (!videoDoc.exists()) {
      //console.log(`비디오 ${videoId}의 정보를 찾을 수 없습니다.`);
      return null;
    }
    
    const videoData = videoDoc.data();
    const lastFetchedAt = videoData.lastFetchedAt;
    
    if (!lastFetchedAt) {
      //console.log(`비디오 ${videoId}의 lastFetchedAt 정보가 없습니다.`);
      return null;
    }
    
    // 2. lastFetchedAt 시간을 기반으로 폴더명 생성 (YYYYMMDDTHH 형식)
    const date = new Date(lastFetchedAt);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const folderName = `${year}${month}${day}T${hour}`;
    
    // 3. comments 컬렉션에서 해당 폴더의 댓글 데이터 조회
    const commentsRef = doc(db, "gameEvent", EVENT_ID, "DripComment", videoId, "comments", folderName);
    const commentsDoc = await getDoc(commentsRef);
    
    if (!commentsDoc.exists()) {
      //console.log(`비디오 ${videoId}의 댓글 데이터를 찾을 수 없습니다. (폴더: ${folderName})`);
      return null;
    }
    
    const commentsData = commentsDoc.data();
    const commentsList = commentsData.list || [];
    
    // 4. 댓글 데이터를 UI에 맞는 형태로 변환
    const formattedComments = commentsList.map((comment, index) => ({
      id: index + 1,
      author: comment.UserName || '익명',
      text: comment.Comment || '',
      likes: comment.LikeCnt || 0,
      replies: comment.ReplyCnt || 0,
      timeAgo: formatTimeAgo(comment.PublishedAt)
    }));
    
    // 5. 좋아요 수 기준으로 정렬 (내림차순)
    return formattedComments.sort((a, b) => b.likes - a.likes);
    
  } catch (error) {
    //console.error("드립 댓글 조회 실패:", error);
    return null;
  }
}

/**
 * 시간을 "~시간 전", "~일 전" 형태로 변환하는 함수
 * @param {string} publishedAt - "YYYY-MM-DD HH:mm:ss" 형태의 시간 문자열
 * @returns {string} 상대적 시간 문자열
 */
function formatTimeAgo(publishedAt) {
  if (!publishedAt) return '시간 정보 없음';
  
  try {
    const publishedTime = new Date(publishedAt);
    const now = new Date();
    const diffMs = now - publishedTime;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}일 전`;
    } else if (diffHours > 0) {
      return `${diffHours}시간 전`;
    } else {
      return '방금 전';
    }
  } catch (error) {
    return '시간 정보 없음';
  }
}



/**
 * 리듬게임 미디어 정보를 Firestore에서 가져오는 함수
 * @returns {Promise<Array>} 리듬게임 미디어 배열
 */
export async function getRhythmMedia() {
  return await safeFirestoreOperation(async () => {
    try {
      const rhythmRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.RHYTHM_GAME);
      const rhythmDoc = await getDoc(rhythmRef);
      
      if (rhythmDoc.exists()) {
        const data = rhythmDoc.data();
        return data.media || [];
      }
      
      // console.warn('리듬게임 미디어 정보를 찾을 수 없습니다. 기본값을 반환합니다.');
      return [];
    } catch (error) {
      // console.error('리듬게임 미디어 정보 가져오기 실패:', error);
      return [];
    }
  }, 'getRhythmMedia');
}

/**
 * Dino 게임 영상 URL을 Firestore에서 가져오는 함수
 * @returns {Promise<string>} 영상 URL 또는 기본 URL
 */
export async function getDinoVideoUrl() {
  return await safeFirestoreOperation(async () => {
    try {
      const dinoRef = doc(db, "gameEvent", EVENT_ID, "config", GAME_STATES.DINO_GAME);
      const dinoDoc = await getDoc(dinoRef);
      
      if (dinoDoc.exists()) {
        const data = dinoDoc.data();
        if (data.videoUrl) {
          return data.videoUrl;
        }
      }
      
      // 기본 영상 URL (파이어스토어에 없을 경우)
      return 'https://www.youtube.com/watch?v=v3kEHHkRvks';
    } catch (error) {
      // console.error('Dino 게임 영상 URL 가져오기 실패:', error);
      // 에러 발생 시 기본 영상 URL
      return 'https://www.youtube.com/watch?v=v3kEHHkRvks';
    }
  }, 'getDinoVideoUrl');
}

/**
 * 메인 비디오 ID를 Firestore에서 가져오는 함수
 * @param {boolean} forceRefresh - 캐시를 무시하고 강제로 새로고침할지 여부
 * @returns {Promise<string>} YouTube 비디오 ID 또는 기본 ID
 */
export async function getMainVideoId(forceRefresh = false) {
  return await safeFirestoreOperation(async () => {
    try {
      // EVENT_ID를 사용하여 올바른 문서 경로로 조회
      const eventDoc = await getDoc(doc(db, "gameEvent", EVENT_ID));
      
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        
        if (eventData.VideoId) {
          return eventData.VideoId;
        }
      }
      
      // 기본 비디오 ID (파이어스토어에 없을 경우)
      return 'nuhslQ_1kOk';
    } catch (error) {
      //console.error('VideoId 로드 실패:', error);
      // 에러 발생 시 기본 비디오 ID
      return 'nuhslQ_1kOk';
    }
  }, 'getMainVideoId');
}

/**
 * 본인 순위 조회 함수 (Cloud Functions 사용)
 * @param {string} gameType - 게임 타입
 * @param {string} phoneHash - 사용자 전화번호 해시
 * @returns {Promise<Object>} 본인 순위 및 주변 순위 정보
 */
export async function getMyRanking(gameType, phoneHash) {
  // console.log(`[getMyRanking] 시작: ${gameType}, ${phoneHash}`);
  const startTime = performance.now();
  
  try {
    // HTTP v2 함수 사용 (더 빠름)
    const response = await fetch(`https://getleaderboard-7llrfl6eaq-du.a.run.app?databaseId=${databaseId}&eventId=${EVENT_ID}&gameType=${gameType}&phoneHash=${phoneHash}&limit=20`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    const totalTime = performance.now() - startTime;
    // console.log(`[getMyRanking] 완료: ${gameType}, 순위: ${data.myRank}, ${totalTime.toFixed(2)}ms`);
    
    return data;
  } catch (error) {
    // console.error(`[getMyRanking] HTTP v2 실패:`, error);
    
    // HTTP v2 실패 시 Cloud Functions로 폴백
    try {
      const result = await getLeaderboardRankingCloud({ 
        gameType,
        eventId: EVENT_ID,
        databaseId: databaseId,
        phoneHash: phoneHash
      });
      const response = result.data;
      
      const totalTime = performance.now() - startTime;
      // console.log(`[getMyRanking] Cloud Functions 완료: ${gameType}, ${totalTime.toFixed(2)}ms`);
      
      return response.data;
    } catch (cloudError) {
      // console.error(`[getMyRanking] Cloud Functions도 실패:`, cloudError);
      throw cloudError;
    }
  }
}

/**
 * 집계 데이터 기반 랭킹 조회 함수 (빠른 조회용)
 * @param {string} gameType - 게임 타입
 * @returns {Promise<Array>} 랭킹 데이터 배열
 */
export async function getLeaderboardRanking(gameType) {
  // console.log(`[getLeaderboardRanking] 시작: ${gameType}`);
  const startTime = performance.now();
  
  try {
    // Cloud Functions에서 집계 랭킹 조회
    const result = await getLeaderboardRankingCloud({ 
      gameType,
      eventId: EVENT_ID,
      databaseId: databaseId
    });
    const response = result.data;
    
    // console.log(`[getLeaderboardRanking] Cloud Functions 완료: ${gameType}`);
    // console.log(`[getLeaderboardRanking] 메타데이터:`, response.metadata);
    
    const totalTime = performance.now() - startTime;
    // console.log(`[getLeaderboardRanking] 전체 완료: ${totalTime.toFixed(2)}ms`);
    
    return response.data; // 실제 랭킹 데이터 반환
  } catch (error) {
    // console.error(`[getLeaderboardRanking] Cloud Functions 실패:`, error);
    
    // 집계 데이터 실패 시 기존 방식으로 폴백
    // console.log(`[getLeaderboardRanking] 폴백: 기존 랭킹 조회`);
    return await getTopRanking(gameType);
  }
}

/**
 * 기존 데이터로 집계 데이터 초기화 함수
 * @param {string} gameType - 게임 타입
 * @returns {Promise<Object>} 초기화 결과
 */
export async function initializeLeaderboard(gameType) {
  // console.log(`[initializeLeaderboard] 시작: ${gameType}`);
  const startTime = performance.now();
  
  try {
    const result = await initializeLeaderboardCloud({ 
      gameType,
      eventId: EVENT_ID,
      databaseId: databaseId
    });
    const response = result.data;
    
    const totalTime = performance.now() - startTime;
    // console.log(`[initializeLeaderboard] 완료: ${gameType}, 처리된 사용자: ${response.processedCount}명, 소요시간: ${totalTime.toFixed(2)}ms`);
    
    return response;
  } catch (error) {
    // console.error(`[initializeLeaderboard] 실패:`, error);
    throw error;
  }
}

